import { useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/ajax/clients/supabase";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { LoadingScreen } from "@/components/LoadingScreen/LoadingScreen";
import { getMfaStatus } from "../mfa";
import { MfaCard } from "./MfaCard";
import { MfaEnroll } from "./MfaEnroll";
import { MfaChallenge } from "./MfaChallenge";

/**
 * Drives the 2FA setup/challenge flow. Decides, from the live session state,
 * whether the user needs to enroll a factor or complete a challenge, and on
 * success sends them on to where they were headed (with a full reload so the
 * newly second-factor-verified session token is used and route guards re-run).
 */
export const MfaSetup = ({ redirectTo }: { redirectTo: string }) => {
  const router = useRouter();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["mfa", "status"],
    queryFn: getMfaStatus,
    retry: false,
  });

  const complete = useCallback(() => {
    queryClient.clear();
    router.navigate({ to: redirectTo as "/", reloadDocument: true });
  }, [router, redirectTo]);

  // If the second factor is already verified (e.g. user navigated here
  // directly), leave.
  useEffect(() => {
    if (data?.secondFactorVerified) {
      complete();
    }
  }, [data, complete]);

  const signOut = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    router.navigate({ to: "/", reloadDocument: true });
  };

  if (isPending) {
    return <LoadingScreen />;
  }

  if (error || !data) {
    return (
      <MfaCard
        icon={<ShieldAlert size={56} />}
        title="Couldn't load security settings"
        description="Please check your connection and try again."
        error={error instanceof Error ? error.message : null}
      >
        <div className="space-y-3">
          <Button onClick={() => refetch()} className="w-full">
            Try again
          </Button>
          <Button onClick={signOut} variant="link" className="w-full">
            Sign out
          </Button>
        </div>
      </MfaCard>
    );
  }

  if (data.secondFactorVerified) {
    return <LoadingScreen />;
  }

  return (
    <>
      {data.verifiedFactors.length > 0 ? (
        <MfaChallenge factors={data.verifiedFactors} onComplete={complete} />
      ) : (
        <MfaEnroll onComplete={complete} />
      )}
      <div className="fixed bottom-4 inset-x-0 flex justify-center">
        <Button onClick={signOut} variant="link" className="text-gray-500">
          Sign out
        </Button>
      </div>
    </>
  );
};
